import React, { useContext } from 'react';
import cn from 'classnames';

import RowTitle, { Name, Ticker } from '../../components/RowTitle/RowTitle';
import CenteredOnDecimal from '../../components/CenteredOnDecimal/CenteredOnDecimal';
import { percentCalc } from '../../components/HighLowIndicator/HighLowIndicator';
import {
    displayShortDateOrNA,
    displaySign,
    displayWithTwoDecimalOrDash
} from '../../utils/Format/Format';

import { GOVERNMENT, RETAIL } from '../../utils/watchlist-settings';
import { isBuyRestricted } from '../../utils/fund-info';

import ascSortIcon from '../../images/watchlist_ascending_sort.svg';
import descSortIcon from '../../images/watchlist_descending_sort.svg';
import unSortIcon from '../../images/watchlist_unsorted_sort.svg';

import checkboxIndeterminate from '../../images/watchlist_checkbox_indeterminate.svg';
import checkboxUnchecked from '../../images/watchlist_checkbox_unchecked.svg';
import checkboxChecked from '../../images/watchlist_checkbox_checked.svg';
import { UserContext } from '../../components/WithUserState/WithUserState';

export const icons = {
    sortAscending: `<img src="${ascSortIcon}" />`,
    sortDescending: `<img src="${descSortIcon}" />`,
    sortUnSort: `<img src="${unSortIcon}" />`,
    checkboxIndeterminate: `<img src="${checkboxIndeterminate}" />`,
    checkboxUnchecked: `<img src="${checkboxUnchecked}" />`,
    checkboxChecked: `<img src="${checkboxChecked}" />`
};

export const DecimalOrDash = ({ value }) => (
    <span className="decimal-or-dash">
        <CenteredOnDecimal>
            {displayWithTwoDecimalOrDash(value)}
        </CenteredOnDecimal>
    </span>
);

export const EvaluatedText = ({ value }) => (
    <span
        className={cn('evaluated-text', {
            positive: value > 0,
            negative: value < 0
        })}
    >
        <CenteredOnDecimal>
            {displaySign(value) + displayWithTwoDecimalOrDash(value)}
        </CenteredOnDecimal>
    </span>
);

export const RowTitleWrapper = ({
    data: {
        moneyMarketType,
        iClass,
        shortName: name,
        ticker,
        selectFund,
        openToNewInvestors,
        openToSubsequentInvestments,
        inceptionDate
    } = {}
}) => {
    const hasRetail = moneyMarketType === RETAIL ? '1' : '';
    const hasGov = moneyMarketType === GOVERNMENT ? '2' : '';
    const hasIClass = iClass ? '^' : '';
    const supLabel = hasRetail.concat(hasGov, hasIClass);
    const { summitTier } = useContext(UserContext) || {};

    return (
        <span
            className={cn('table-wrapping', {
                disabled: isBuyRestricted(
                    openToNewInvestors,
                    openToSubsequentInvestments
                )
            })}
        >
            <RowTitle
                name={name}
                ticker={ticker}
                container="ag"
                isSelectFund={selectFund}
                etf={false}
                openToNewInvestors={openToNewInvestors}
                openToSubsequentInvestments={openToSubsequentInvestments}
                summitTier={summitTier}
                iClass={iClass}
                inceptionDate={displayShortDateOrNA(inceptionDate)}
            >
                <span className="row-title-content">
                    <Name>{name}</Name>
                    <Ticker>({ticker})</Ticker>
                    <sup>{supLabel}</sup>
                </span>
            </RowTitle>
        </span>
    );
};

export const NoRowRenderer = () => (
    <div className="ag-overlay-loading-center">
        No funds have been added to this watchlist.
    </div>
);

export const fundNameComparator = (value1, value2, node1, node2) =>
    node1.data.name.localeCompare(node2.data.name);

export const highLowComparator = (value1, value2, node1, node2) => {
    const {
        data: {
            twelveMonthHighPrice: { price: aHigh },
            twelveMonthLowPrice: { price: aLow },
            price: { price: aPrice }
        }
    } = node1;
    const {
        data: {
            twelveMonthHighPrice: { price: bHigh },
            twelveMonthLowPrice: { price: bLow },
            price: { price: bPrice }
        }
    } = node2;
    const aCalc = percentCalc(aPrice, aLow, aHigh);
    const bCalc = percentCalc(bPrice, bLow, bHigh);
    return aCalc - bCalc;
};
// `screenIsMobileWidth` instead of `isMobileWidth()` is to simplify testing by removing a dependency
export const calculateRowHeight = (
    screenIsMobileWidth,
    name = '',
    ticker = '',
    iClass = false,
    openToNewInvestors = true
) => {
    let minRowHeight = iClass ? 80 : 60;

    // ETF
    if (ticker && ticker.length === 4) {
        minRowHeight = 70;
    }

    if (!openToNewInvestors) {
        minRowHeight += 20;
    }
    if (name.length > 40) {
        minRowHeight += 20;
    }

    if (screenIsMobileWidth) {
        const padding = 16;
        const lineHeight = 18.4;
        const charactersPerLine = 13;
        const wrappingBuffer = 10;
        const wrappingLinesCount =
            (`${ticker} ${name}`.length + wrappingBuffer) / charactersPerLine;

        return Math.max(
            Math.ceil(padding + lineHeight * wrappingLinesCount),
            minRowHeight
        );
    }

    return minRowHeight;
};
